import { useEffect, useRef } from "react";

/**
 * Calls `fetcher` on mount AND whenever the browser tab regains focus.
 *
 * Use-case: keep the storefront in sync with admin-side changes (e.g. a
 * category just deleted in /admin) the moment the user comes back to the
 * storefront tab. The API is cached for 5–30 s upstream, so refetching on
 * focus gives admin changes a tight ~30 s worst-case propagation window
 * without polluting the UX with constant polling.
 *
 * Guard against double-invocation in React 18 StrictMode by keeping a ref.
 *
 * @param {() => (void|Promise<void>)} fetcher  side-effect to re-run on focus
 * @param {Array}                       deps    dependency list (like useEffect)
 */
export default function useStoreRefresh(fetcher, deps = []) {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const run = () => {
      try { fetcherRef.current(); } catch { /* noop */ }
    };
    run();
    // Rerun when the tab comes back into focus (user returns from admin tab).
    const onVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") run();
    };
    window.addEventListener("focus", run);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", run);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, deps);
}
